import { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import PageTracker from './components/PageTracker';

import LanguageRedirector from './components/LanguageRedirector';

import React from 'react';

// ── Static Imports for Global Wrappers ──
import DemoBanner from './components/DemoBanner';
import PromotionalModal from './components/PromotionalModal';
import LanguageRouteWrapper from './components/LanguageRouteWrapper';
import EnglishRouteWrapper from './components/EnglishRouteWrapper';

// ── lazyWithPreload Helper ──
// This ensures that preloaded components render synchronously and don't suspend during hydration.
function lazyWithPreload<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  let Loaded: T | null = null;
  const Lazy = lazy(factory);
  const Wrapper = (props: React.ComponentProps<T>) => {
    return Loaded ? React.createElement(Loaded, props) : React.createElement(Lazy, props);
  };
  Wrapper.preload = () => factory().then(m => { Loaded = m.default; });
  return Wrapper as unknown as T & { preload: () => Promise<void> };
}

// ── Other Lazy Components ──
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const DashboardLayout = lazy(() => import('./components/DashboardLayout'));
const LazyToaster = lazy(() => import('react-hot-toast').then(m => ({ default: m.Toaster })));

// ── Prerendered Public Routes ──
export const LandingPage = lazyWithPreload(() => import('./pages/LandingPage'));
export const LoginPage = lazyWithPreload(() => import('./pages/LoginPage'));
export const SignupPage = lazyWithPreload(() => import('./pages/SignupPage'));
export const VerifyEmailPage = lazyWithPreload(() => import('./pages/VerifyEmailPage'));
export const ForgotPasswordPage = lazyWithPreload(() => import('./pages/ForgotPasswordPage'));
export const ResetPasswordPage = lazyWithPreload(() => import('./pages/ResetPasswordPage'));
export const AcceptInvitePage = lazyWithPreload(() => import('./pages/AcceptInvitePage'));
export const PrivacyPolicyPage = lazyWithPreload(() => import('./pages/PrivacyPolicyPage'));
export const TermsOfServicePage = lazyWithPreload(() => import('./pages/TermsOfServicePage'));
export const CookiePolicyPage = lazyWithPreload(() => import('./pages/CookiePolicyPage'));
export const FAQPage = lazyWithPreload(() => import('./pages/FAQPage'));
export const BlogListPage = lazyWithPreload(() => import('./pages/BlogListPage'));
export const BlogCategoryPage = lazyWithPreload(() => import('./pages/BlogCategoryPage'));
export const BlogPostPage = lazyWithPreload(() => import('./pages/BlogPostPage'));
export const FeaturePage = lazyWithPreload(() => import('./pages/FeaturePage'));
export const WalkthroughPage = lazyWithPreload(() => import('./pages/WalkthroughPage'));
export const ReviewGatePage = lazyWithPreload(() => import('./pages/ReviewGatePage'));
export const AboutPage = lazyWithPreload(() => import('./pages/AboutPage'));
export const SecurityPage = lazyWithPreload(() => import('./pages/SecurityPage'));
export const NotFoundPage = lazyWithPreload(() => import('./pages/NotFoundPage'));

// Dashboard pages — NOT prerendered, no need for named imports
const DashboardHome = lazy(() => import('./pages/DashboardHome'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const UsagePage = lazy(() => import('./pages/UsagePage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminContactPage = lazy(() => import('./pages/AdminContactPage'));
const PaymentCheckoutPage = lazy(() => import('./pages/PaymentCheckoutPage'));
const AdminBlogPage = lazy(() => import('./pages/AdminBlogPage'));
const ReviewInsightsPage = lazy(() => import('./pages/ReviewInsightsPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const FeedbackInboxPage = lazy(() => import('./pages/FeedbackInboxPage'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--color-bg)' }}>
    <div style={{ width: '40px', height: '40px', border: '3px solid var(--color-border)', borderTopColor: 'var(--color-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

const ClientOnlyToaster = () => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);
  if (!isMounted) return null;
  return (
    <Suspense fallback={null}>
      <LazyToaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
          },
        }}
      />
    </Suspense>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageTracker />
      <LanguageRedirector />
      <AuthProvider>
          <DemoBanner />
          <PromotionalModal />
          <Routes>
          {/* Public routes (Default) */}
          <Route element={<EnglishRouteWrapper />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/category/:slug" element={<BlogCategoryPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/features/:slug" element={<FeaturePage />} />
            <Route path="/walkthroughs/:slug" element={<WalkthroughPage />} />
            <Route path="/r/:slug" element={<ReviewGatePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/security" element={<SecurityPage />} />
          </Route>

          <Route path="/:lang" element={<LanguageRouteWrapper />}>
            <Route index element={<LandingPage />} />
            <Route path="home" element={<Navigate to="/" replace />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="signup" element={<SignupPage />} />
            <Route path="verify-email" element={<VerifyEmailPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="accept-invite" element={<AcceptInvitePage />} />
            <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="terms-of-service" element={<TermsOfServicePage />} />
            <Route path="cookie-policy" element={<CookiePolicyPage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="blog" element={<BlogListPage />} />
            <Route path="blog/category/:slug" element={<BlogCategoryPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            <Route path="features/:slug" element={<FeaturePage />} />
            <Route path="walkthroughs/:slug" element={<WalkthroughPage />} />
            <Route path="r/:slug" element={<ReviewGatePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Protected routes — wrapped in Suspense because these use plain lazy() */}
          <Route element={<Suspense fallback={<PageLoader />}><ProtectedRoute /></Suspense>}>
            <Route path="/onboarding" element={<Suspense fallback={<PageLoader />}><OnboardingPage /></Suspense>} />
            <Route element={<DashboardLayout />} >
              <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><DashboardHome /></Suspense>} />
              <Route path="/dashboard/reviews" element={<Suspense fallback={<PageLoader />}><ReviewsPage /></Suspense>} />
              <Route path="/dashboard/locations" element={<Suspense fallback={<PageLoader />}><LocationsPage /></Suspense>} />
              <Route path="/dashboard/analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>} />
              <Route path="/dashboard/insights" element={<Suspense fallback={<PageLoader />}><ReviewInsightsPage /></Suspense>} />
              <Route path="/dashboard/campaigns" element={<Suspense fallback={<PageLoader />}><CampaignsPage /></Suspense>} />
              <Route path="/dashboard/feedback" element={<Suspense fallback={<PageLoader />}><FeedbackInboxPage /></Suspense>} />
              <Route path="/dashboard/usage" element={<Suspense fallback={<PageLoader />}><UsagePage /></Suspense>} />
              <Route path="/dashboard/billing" element={<Suspense fallback={<PageLoader />}><BillingPage /></Suspense>} />
              <Route path="/dashboard/settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
              <Route path="/dashboard/audit-log" element={<Suspense fallback={<PageLoader />}><AuditLogPage /></Suspense>} />
              <Route path="/dashboard/notifications" element={<Suspense fallback={<PageLoader />}><NotificationsPage /></Suspense>} />
              <Route path="/dashboard/checkout" element={<Suspense fallback={<PageLoader />}><PaymentCheckoutPage /></Suspense>} />
              <Route path="/dashboard/admin" element={<Suspense fallback={<PageLoader />}><AdminPage /></Suspense>} />
              <Route path="/dashboard/admin/contacts" element={<Suspense fallback={<PageLoader />}><AdminContactPage /></Suspense>} />
              <Route path="/dashboard/admin/blog" element={<Suspense fallback={<PageLoader />}><AdminBlogPage /></Suspense>} />
            </Route>
          </Route>

          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <ClientOnlyToaster />
      </AuthProvider>
    </BrowserRouter>
  );
}
